import React, { useId, useState } from "react";
import { describeLoad, dollars } from "./loadFormat";

// Closed by default so the price stays the only thing on the page.
export default function LoadDetails({
  load,
  postedRateCents,
  defaultOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  const { origin, dest, stops, facts, stopSummary } = describeLoad(load);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex w-full flex-col gap-2 text-left !shadow-none !bg-transparent !border-0 !rounded-none !px-4 !py-3.5 !text-slate-900 hover:!translate-y-0"
      >
        <span className="block text-balance font-bold text-[20px] leading-tight break-words">
          {origin} <span className="text-slate-400">→</span> {dest}
        </span>
        <span className="flex w-full items-center justify-between gap-3 font-[Roboto] text-[14px] font-normal text-slate-500">
          <span className="whitespace-nowrap">
            {postedRateCents ? (
              <>
                Posted{" "}
                <span className="font-bold text-emerald-700">
                  {dollars(postedRateCents / 100)}
                </span>
              </>
            ) : (
              "No posted rate"
            )}
          </span>
          <span
            className={`inline-flex shrink-0 items-center gap-1 rounded-full py-1 pl-3 pr-2 text-[13.5px] font-medium transition-colors duration-200 ${
              open
                ? "bg-indigo-50 text-indigo-700"
                : "bg-slate-100 text-slate-700"
            }`}
          >
            {open ? "Hide details" : "See details"}
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              className={`h-4 w-4 transition-transform duration-300 motion-reduce:transition-none ${
                open ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3.5 6l4.5 4.5L12.5 6" />
            </svg>
          </span>
        </span>
      </button>

      <div
        id={bodyId}
        aria-hidden={!open}
        inert={open ? undefined : ""}
        className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="border-t border-slate-200 px-4 pt-3.5 pb-4 font-[Roboto]">
            {stopSummary && (
              <p className="mb-2 text-[12px] font-bold uppercase tracking-[0.07em] text-amber-800">
                {stopSummary}
              </p>
            )}
            <ol className="list-none m-0 p-0 mb-4">
              {stops.map((stop, i) => {
                const last = i === stops.length - 1;
                return (
                  <li
                    key={i}
                    className={`relative grid grid-cols-[14px_minmax(0,1fr)] gap-x-3 ${last ? "" : "pb-3"}`}
                  >
                    {!last && (
                      <span
                        aria-hidden="true"
                        className="absolute left-[6px] top-[17px] bottom-0 w-[2px] bg-slate-200"
                      />
                    )}
                    <span
                      aria-hidden="true"
                      className={`mt-[3px] h-[14px] w-[14px] rounded-full ring-[3px] ${
                        stop.pick
                          ? "bg-indigo-600 ring-indigo-100"
                          : "bg-emerald-500 ring-emerald-100"
                      }`}
                    />
                    <div className="min-w-0">
                      <p className="mb-0 text-[11.5px] font-bold uppercase tracking-[0.07em] text-slate-500">
                        {stop.label}
                      </p>
                      <p className="mb-0 font-[Barlow_Condensed] text-[18px] font-bold leading-tight text-slate-900 break-words">
                        {stop.place}
                      </p>
                      {stop.when && (
                        <p className="mb-0 text-[14px] text-slate-700">
                          {stop.when}
                        </p>
                      )}
                      {stop.details && (
                        <p className="mb-0 text-[12.5px] text-slate-500">
                          {stop.details}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>

            {facts.length > 0 && (
              <dl className="m-0 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200">
                {facts.map((f, i) => (
                  <div
                    key={f.label}
                    className={`bg-white px-3 py-2 ${
                      i === facts.length - 1 && facts.length % 2
                        ? "col-span-2"
                        : ""
                    }`}
                  >
                    <dt className="text-[12px] text-slate-500">{f.label}</dt>
                    <dd className="m-0 font-[Barlow_Condensed] text-[17px] font-bold text-slate-900 tabular-nums">
                      {f.value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
